import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { categoriaDe, resumoPendencias, textoPrazo } from '../publicacao.definitions';
import type { SituacaoPrazo } from '../publicacao.definitions';
import type { Publicacao } from '../publicacao.types';

interface PendenciasCienciaFaixaProps {
  pendencias: Array<{ publicacao: Publicacao; situacao: SituacaoPrazo }>;
  linkDe: (publicacao: Publicacao) => string;
  /** Texto do título (padrão: publicações). */
  rotulo?: { um: string; varios: (n: number) => string };
}

const SELO: Record<SituacaoPrazo['tipo'], string> = {
  atrasada: 'rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-700',
  vence: 'rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800',
  sem_prazo: 'text-xs text-raro-gray-500',
};

const ROTULO_PADRAO = { um: '1 publicação aguardando sua ciência', varios: (n: number) => `${n} publicações aguardando sua ciência` };

/** Faixa da Início: sempre começa fechada (1 linha com o resumo); a lista só abre ao clicar —
 * com 2 ou 15 pendentes ocupa o mesmo espaço. */
export function PendenciasCienciaFaixa({ pendencias, linkDe, rotulo = ROTULO_PADRAO }: PendenciasCienciaFaixaProps) {
  const [aberta, setAberta] = useState(false);
  if (pendencias.length === 0) return null;
  const total = pendencias.length;

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50">
      <div className="flex items-center gap-3 px-5 py-4">
        <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">
            {total === 1 ? rotulo.um : rotulo.varios(total)}
          </p>
          <p className="text-xs text-amber-800">{resumoPendencias(pendencias)}</p>
        </div>
        {total === 1 ? (
          <Link
            to={linkDe(pendencias[0].publicacao)}
            className="shrink-0 rounded-full bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-700"
          >
            Ver agora
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => setAberta((atual) => !atual)}
            aria-expanded={aberta}
            className="shrink-0 rounded-full border border-amber-300 bg-white px-4 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100"
          >
            {aberta ? 'Ocultar' : `Ver as ${total}`}
          </button>
        )}
      </div>
      {aberta && total > 1 && (
        <ul className="divide-y divide-amber-200 border-t border-amber-200 text-sm">
          {pendencias.map(({ publicacao, situacao }) => (
            <li key={publicacao.id}>
              <Link
                to={linkDe(publicacao)}
                className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-amber-100/60"
              >
                <span className="min-w-0 truncate">
                  <b className="font-semibold text-raro-gray-900">{publicacao.titulo}</b>{' '}
                  <span className="text-xs text-raro-gray-500">
                    · {publicacao.tipo === 'documento' ? 'Documento · ' : ''}
                    {categoriaDe(publicacao).label}
                  </span>
                </span>
                <span className={`shrink-0 ${SELO[situacao.tipo]}`}>{textoPrazo(situacao)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
